/**
 * Dialog de configuração do node Template WhatsApp Oficial
 * 
 * Três etapas:
 * 1. Selecionar inbox do WhatsApp Oficial
 * 2. Selecionar template aprovado
 * 3. Configurar variáveis do BODY, HEADER e BUTTONS
 * 
 * Reutiliza lógica do SendTemplateDialog para seleção e variáveis
 * 
 * RELEASE: 1.1.0 - 2026-02-07 - Correção robusta de scroll com overflow nativo
 * RELEASE: 1.0.0 - 2026-01-31 - Novo dialog de configuração
 * 
 * NOTA TÉCNICA: Este modal utiliza scroll NATIVO (overflow-y-auto) em vez de
 * Radix ScrollArea para garantir funcionamento correto de wheel/trackpad e
 * visibilidade da barra de rolagem em todas as etapas. O DialogContent usa
 * h-[85vh] + min-h-0 para criar uma "caixa" real que permite ao flexbox
 * calcular altura disponível para o body rolável.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Search,
  RefreshCw,
  FileText,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Inbox,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MessageTemplateConfig } from '@/types/flows';
import { InputWithVariables } from '../InputWithVariables';

/**
 * Interface para template WhatsApp
 */
interface WhatsAppTemplate {
  id: string;
  inbox_id: string;
  template_id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  components: any;
  variables: any;
}

/**
 * Interface para inbox do WhatsApp Oficial
 */
interface OfficialInbox {
  id: string;
  name: string;
  identifier: string;
  provider_slug: string;
}

interface ConfigureTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  initialConfig?: Partial<MessageTemplateConfig>;
  onSave: (config: MessageTemplateConfig) => void;
  /** ID do node para exibição e cópia */
  nodeId?: string;
  /** Apelido salvo anteriormente */
  defaultNickname?: string;
}

/**
 * Extrai componente por tipo do array de components
 */
const getComponent = (components: any[], type: string) => {
  if (!components || !Array.isArray(components)) return null;
  return components.find(c => c.type === type);
};

/**
 * Extrai variáveis do texto (formato {{1}}, {{2}}, etc.)
 */
const extractVariables = (text: string): string[] => {
  if (!text) return [];
  const matches = text.match(/\{\{(\d+)\}\}/g);
  if (!matches) return [];
  return [...new Set(matches)].sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ''));
    const numB = parseInt(b.replace(/\D/g, ''));
    return numA - numB;
  });
};

/**
 * Labels em português para categorias
 */
const categoryLabels: Record<string, string> = {
  UTILITY: 'Utilitário',
  MARKETING: 'Marketing',
  AUTHENTICATION: 'Autenticação',
};

/**
 * Cores por categoria
 */
const categoryColors: Record<string, string> = {
  MARKETING: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  UTILITY: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  AUTHENTICATION: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
};

export function ConfigureTemplateDialog({
  open,
  onOpenChange,
  tenantId,
  initialConfig,
  onSave,
  nodeId,
  defaultNickname,
}: ConfigureTemplateDialogProps) {
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  // Estados de navegação
  const [step, setStep] = useState<'inbox' | 'template' | 'variables'>('inbox');
  
  // Estados de dados
  const [inboxes, setInboxes] = useState<OfficialInbox[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Estados de seleção
  const [selectedInbox, setSelectedInbox] = useState<OfficialInbox | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  
  // Estados de variáveis
  const [bodyVariables, setBodyVariables] = useState<Record<string, string>>({});
  const [headerVariable, setHeaderVariable] = useState('');
  const [buttonVariables, setButtonVariables] = useState<Record<number, string>>({});

  // Carrega inboxes do WhatsApp Oficial quando abre
  useEffect(() => {
    if (open && tenantId) {
      fetchOfficialInboxes();
      
      // Restaura config inicial se existir
      if (initialConfig?.inbox_id && initialConfig?.template_id) {
        restoreFromConfig(initialConfig as MessageTemplateConfig);
      }
    }
  }, [open, tenantId]);

  // Reset quando fecha
  useEffect(() => {
    if (!open) {
      setStep('inbox');
      setSelectedInbox(null);
      setSelectedTemplate(null);
      setBodyVariables({});
      setHeaderVariable('');
      setButtonVariables({});
      setSearchQuery('');
    }
  }, [open]);

  /**
   * Restaura estado a partir de config existente
   */
  const restoreFromConfig = async (config: MessageTemplateConfig) => {
    // Busca inbox
    const { data: inbox } = await supabase
      .from('inboxes')
      .select('id, name, identifier')
      .eq('id', config.inbox_id)
      .single();
    
    if (inbox) {
      setSelectedInbox({
        id: inbox.id,
        name: inbox.name,
        identifier: inbox.identifier,
        provider_slug: 'notificame', // Assumindo WhatsApp Oficial
      });
      
      // Carrega templates da inbox
      const { data: templates } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .eq('inbox_id', config.inbox_id)
        .eq('status', 'APPROVED');
      
      if (templates) {
        setTemplates(templates);
        
        // Encontra template selecionado
        const template = templates.find(t => t.id === config.template_id);
        if (template) {
          setSelectedTemplate(template);
          setBodyVariables(config.body_variables || {});
          setHeaderVariable(config.header_variable || '');
          
          // Restaura variáveis de botões
          if (config.button_variables) {
            const btnVars: Record<number, string> = {};
            config.button_variables.forEach(bv => {
              if (bv.type === 'url' && bv.url_suffix) {
                btnVars[bv.index] = bv.url_suffix;
              }
            });
            setButtonVariables(btnVars);
          }
          
          setStep('variables');
        } else {
          setStep('template');
        }
      }
    }
  };

  /**
   * Busca inboxes do WhatsApp Oficial (notificame, meta-whatsapp)
   */
  const fetchOfficialInboxes = async () => {
    setLoading(true);
    try {
      // Busca inboxes com provider do WhatsApp Oficial
      const { data, error } = await supabase
        .from('inboxes')
        .select(`
          id, name, identifier, status,
          provider_instances!inner(
            api_providers!inner(slug)
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .in('provider_instances.api_providers.slug', ['notificame', 'meta-whatsapp']);

      if (error) throw error;
      
      const officialInboxes: OfficialInbox[] = (data || []).map((inbox: any) => ({
        id: inbox.id,
        name: inbox.name,
        identifier: inbox.identifier,
        provider_slug: inbox.provider_instances?.api_providers?.slug || 'notificame',
      }));
      
      setInboxes(officialInboxes);
    } catch (error) {
      console.error('[ConfigureTemplateDialog] Erro ao buscar inboxes:', error);
      toast.error('Erro ao carregar canais oficiais');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Busca templates aprovados da inbox selecionada
   */
  const fetchTemplates = async (inboxId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .eq('inbox_id', inboxId)
        .eq('status', 'APPROVED')
        .order('name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('[ConfigureTemplateDialog] Erro ao buscar templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sincroniza templates com o provider
   */
  const handleSync = async () => {
    if (!selectedInbox) return;
    
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-templates', {
        body: { inbox_id: selectedInbox.id },
      });

      if (error) throw error;
      
      toast.success(`Templates sincronizados: ${data?.synced || 0}`);
      await fetchTemplates(selectedInbox.id);
    } catch (error) {
      console.error('[ConfigureTemplateDialog] Erro ao sincronizar:', error);
      toast.error('Erro ao sincronizar templates');
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Handler para selecionar inbox
   */
  const handleSelectInbox = (inbox: OfficialInbox) => {
    setSelectedInbox(inbox);
    setSelectedTemplate(null);
    setBodyVariables({});
    fetchTemplates(inbox.id);
    setStep('template');
  };

  /**
   * Handler para selecionar template
   */
  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    setBodyVariables({});
    setHeaderVariable('');
    setButtonVariables({});
    setStep('variables');
  };

  /**
   * Handler para salvar configuração
   */
  const handleSave = () => {
    if (!selectedInbox || !selectedTemplate) return;

    // Valida variáveis obrigatórias do body
    const missingVars = templateBodyVariables.filter(v => {
      const key = v.replace(/[{}]/g, '');
      return !bodyVariables[key]?.trim();
    });

    if (missingVars.length > 0) {
      toast.error('Preencha todas as variáveis da mensagem');
      return;
    }

    // Monta configuração
    const config: MessageTemplateConfig = {
      inbox_id: selectedInbox.id,
      inbox_name: selectedInbox.name,
      template_id: selectedTemplate.id,
      template_name: selectedTemplate.name,
      template_language: selectedTemplate.language,
      template_category: selectedTemplate.category,
      body_variables: bodyVariables,
      template_body_text: bodyText,
      template_has_buttons: dynamicButtons.length > 0,
    };

    // Adiciona header se configurado
    if (headerVariable) {
      config.header_variable = headerVariable;
      config.header_type = 'TEXT'; // Por enquanto só texto
    }

    // Adiciona variáveis de botões URL
    if (Object.keys(buttonVariables).length > 0) {
      config.button_variables = Object.entries(buttonVariables).map(([idx, suffix]) => ({
        index: parseInt(idx),
        type: 'url' as const,
        url_suffix: suffix,
      }));
    }

    /** Inclui apelido no config antes de salvar */
    (config as any).nickname = nicknameRef.current?.getNickname();
    onSave(config);
    onOpenChange(false);
  };

  // Filtra templates pela busca
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(t => 
      t.name.toLowerCase().includes(query) ||
      t.category.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);

  // Extrai texto do body do template selecionado
  const bodyText = useMemo(() => {
    if (!selectedTemplate?.components) return '';
    const body = getComponent(selectedTemplate.components, 'BODY');
    return body?.text || '';
  }, [selectedTemplate]);

  // Lista de variáveis do body
  const templateBodyVariables = useMemo(() => {
    return extractVariables(bodyText);
  }, [bodyText]);

  // Detecta botões dinâmicos URL
  const dynamicButtons = useMemo(() => {
    if (!selectedTemplate?.components) return [];
    const buttonsComp = getComponent(selectedTemplate.components, 'BUTTONS');
    if (!buttonsComp?.buttons) return [];
    
    return buttonsComp.buttons
      .map((btn: any, index: number) => {
        const isDynamic = btn.type === 'URL' && btn.url?.includes('{{1}}');
        if (!isDynamic) return null;
        return {
          index,
          text: btn.text || `Botão ${index + 1}`,
          url: btn.url,
          baseUrl: btn.url.replace('{{1}}', ''),
        };
      })
      .filter(Boolean);
  }, [selectedTemplate]);

  // Detecta header dinâmico
  const hasHeaderVariable = useMemo(() => {
    if (!selectedTemplate?.components) return false;
    const header = getComponent(selectedTemplate.components, 'HEADER');
    if (!header) return false;
    return header.format === 'TEXT' && header.text?.includes('{{');
  }, [selectedTemplate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* 
        h-[85vh]: Cria altura fixa para o flex calcular corretamente o espaço disponível.
        min-h-0: Evita comportamento padrão do flex que impede filhos de encolherem.
        overflow-hidden: Garante que apenas o body interno tenha scroll (evita dupla rolagem).
      */}
      <DialogContent className="max-w-xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
        {/* shrink-0: Impede que o header encolha em cenários de layout apertado */}
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-500" />
            {step === 'inbox' && 'Selecionar Canal'}
            {step === 'template' && 'Selecionar Template'}
            {step === 'variables' && `Configurar: ${selectedTemplate?.name}`}
          </DialogTitle>
        </DialogHeader>

        {/* Campo ID do Node e Apelido */}
        {nodeId && (
          <div className="shrink-0 px-1">
            <NodeIdField ref={nicknameRef} nodeId={nodeId} defaultNickname={defaultNickname} />
          </div>
        )}

        {/* Etapa 1: Seleção de Inbox */}
        {step === 'inbox' && (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Alerta informativo - shrink-0 para não encolher */}
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg shrink-0">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-muted-foreground">
                Templates só podem ser enviados via WhatsApp Oficial
              </span>
            </div>
            
            {/* 
              Scroll NATIVO em vez de Radix ScrollArea para garantir:
              - Wheel/trackpad funcionam corretamente
              - Barra de rolagem visível
              - overscroll-contain: evita scroll "vazar" para o fundo do modal
            */}
            <div 
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-2 pb-2"
              style={{ scrollbarGutter: 'stable' }}
            >
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : inboxes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Inbox className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum canal do WhatsApp Oficial encontrado</p>
                  <p className="text-xs mt-1">Configure uma inbox com Notificame ou Meta WABA</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {inboxes.map((inbox) => (
                    <button
                      key={inbox.id}
                      onClick={() => handleSelectInbox(inbox)}
                      className={cn(
                        "w-full p-4 text-left rounded-lg border transition-colors",
                        "hover:bg-accent hover:border-primary/50",
                        selectedInbox?.id === inbox.id && "border-primary bg-primary/5"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-emerald-500/10 p-2 rounded">
                          <Inbox className="h-5 w-5 text-emerald-500" />
                        </div>
                        <div>
                          <div className="font-medium">{inbox.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {inbox.identifier}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Etapa 2: Seleção de Template */}
        {step === 'template' && (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Header com busca e sync - shrink-0 para não encolher */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('inbox')}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Voltar
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={syncing}
              >
                <RefreshCw className={cn("h-4 w-4", syncing && "animate-spin")} />
              </Button>
            </div>
            
            {/* 
              Scroll NATIVO em vez de Radix ScrollArea para garantir:
              - Wheel/trackpad funcionam corretamente
              - Barra de rolagem visível
              - overscroll-contain: evita scroll "vazar" para o fundo do modal
            */}
            <div 
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-2 pb-2"
              style={{ scrollbarGutter: 'stable' }}
            >
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum template aprovado encontrado</p>
                  <p className="text-xs mt-1">Sincronize ou aguarde aprovação</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredTemplates.map((template) => {
                    const body = getComponent(template.components, 'BODY');
                    const bodyPreview = body?.text?.substring(0, 100) + (body?.text?.length > 100 ? '...' : '');
                    
                    return (
                      <button
                        key={template.id}
                        onClick={() => handleSelectTemplate(template)}
                        className={cn(
                          "w-full p-4 text-left rounded-lg border transition-colors",
                          "hover:bg-accent hover:border-primary/50"
                        )}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="font-medium">{template.name}</div>
                          <div className="flex gap-1">
                            <Badge 
                              variant="outline" 
                              className={cn("text-xs", categoryColors[template.category])}
                            >
                              {categoryLabels[template.category] || template.category}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {template.language}
                            </Badge>
                          </div>
                        </div>
                        {bodyPreview && (
                          <div className="text-sm text-muted-foreground">
                            {bodyPreview}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Etapa 3: Configurar Variáveis */}
        {step === 'variables' && selectedTemplate && (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Header - shrink-0 para não encolher */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('template')}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Voltar
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex gap-1">
                <Badge 
                  variant="outline" 
                  className={cn("text-xs", categoryColors[selectedTemplate.category])}
                >
                  {categoryLabels[selectedTemplate.category] || selectedTemplate.category}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {selectedTemplate.language}
                </Badge>
              </div>
            </div>
            
            {/* 
              Scroll NATIVO em vez de Radix ScrollArea para garantir:
              - Wheel/trackpad funcionam corretamente
              - Barra de rolagem visível
              - overscroll-contain: evita scroll "vazar" para o fundo do modal
            */}
            <div 
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-2 pb-2"
              style={{ scrollbarGutter: 'stable' }}
            >
              <div className="space-y-4">
                {/* Preview do template */}
                <div className="p-3 bg-muted/50 rounded-lg">
                  <Label className="text-xs text-muted-foreground mb-1 block">Preview do Template</Label>
                  <div className="text-sm whitespace-pre-wrap">
                    {bodyText || 'Sem conteúdo'}
                  </div>
                </div>
                
                <Separator />
                
                {/* Variáveis do BODY */}
                {templateBodyVariables.length > 0 && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Variáveis da Mensagem</Label>
                    {templateBodyVariables.map((variable) => {
                      const key = variable.replace(/[{}]/g, '');
                      return (
                        <div key={key}>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            {variable}
                          </Label>
                          <InputWithVariables
                            tenantId={tenantId}
                            value={bodyVariables[key] || ''}
                            onChange={(value) => setBodyVariables(prev => ({
                              ...prev,
                              [key]: value,
                            }))}
                            placeholder={`Valor para ${variable}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Variável do HEADER */}
                {hasHeaderVariable && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Variável do Cabeçalho</Label>
                      <InputWithVariables
                        tenantId={tenantId}
                        value={headerVariable}
                        onChange={setHeaderVariable}
                        placeholder="Valor para o cabeçalho"
                      />
                    </div>
                  </>
                )}
                
                {/* Variáveis dos BOTÕES */}
                {dynamicButtons.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Variáveis dos Botões</Label>
                      {dynamicButtons.map((btn: any) => (
                        <div key={btn.index}>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            {btn.text} - Sufixo da URL
                          </Label>
                          <div className="flex gap-2 items-center">
                            <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                              {btn.baseUrl}
                            </span>
                            <InputWithVariables
                              tenantId={tenantId}
                              value={buttonVariables[btn.index] || ''}
                              onChange={(value) => setButtonVariables(prev => ({
                                ...prev,
                                [btn.index]: value,
                              }))}
                              placeholder="Sufixo"
                              className="flex-1"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                
                {/* Mensagem quando não há variáveis */}
                {templateBodyVariables.length === 0 && !hasHeaderVariable && dynamicButtons.length === 0 && (
                  <div className="text-center py-4 text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                    <p>Este template não possui variáveis</p>
                    <p className="text-xs">Clique em Salvar para confirmar</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* shrink-0: Impede que o footer encolha em cenários de layout apertado */}
        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          {step === 'variables' && (
            <Button onClick={handleSave}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Salvar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
